<template>
  <v-form ref="form" class="form">
    <v-row>
      <v-col class="py-0" cols="12">
        <v-autocomplete
          v-model="type"
          :label="$t('source-adapters.type')"
          :loading="resourceLoading"
          :items="types"
          item-text="title"
          item-value="value"
          :disabled="!!sourceAdapter.id"
          :rules="
            resourceLoading ? [] : [rules.required, backEndValidation('name')]
          "
        ></v-autocomplete>
      </v-col>
      <v-col class="py-0" cols="12">
        <v-text-field
          v-model="sourceAdapter.authentication"
          :label="$t('source-adapters.authentication')"
          readonly
          :loading="resourceLoading"
        ></v-text-field>
      </v-col>
      <v-col class="py-0" cols="12">
        <v-text-field
          v-model="sourceAdapter.name"
          :label="$t('source-adapters.name')"
          :loading="resourceLoading"
          :rules="
            resourceLoading ? [] : [rules.required, backEndValidation('name')]
          "
          :readonly="readonly"
        ></v-text-field>
      </v-col>
      <v-col class="pb-5" cols="12">
        <p>{{ $t("source-adapters.config") }}</p>
        <v-tabs v-model="tabIndex">
          <v-tab>Table</v-tab>
          <v-tab>Json</v-tab>
        </v-tabs>
        <div class="pr-4 tabs-max-width">
          <v-divider></v-divider>
        </div>
        <v-tabs-items v-model="tabIndex">
          <v-tab-item>
            <ConfigSourceAdapter
              v-if="tabIndex === 0"
              :config-string.sync="config"
              :type="type"
            ></ConfigSourceAdapter>
          </v-tab-item>
          <v-tab-item>
            <codemirror
              class="codemirror-item"
              ref="codemirror"
              v-model="config"
              :options="options"
            ></codemirror>
            <v-textarea
              class="config-error-bag"
              :loading="resourceLoading"
              :rules="resourceLoading ? [] : [backEndValidation('config')]"
              :readonly="readonly"
              :label="$t('source-adapters.config')"
            ></v-textarea>
          </v-tab-item>
        </v-tabs-items>
      </v-col>
      <v-col class="py-0" cols="12">
        <v-textarea
          v-model="sourceAdapter.notices"
          :label="$t('source-adapters.notices')"
          :loading="resourceLoading"
          :rules="resourceLoading ? [] : [backEndValidation('notices')]"
          :readonly="readonly"
        ></v-textarea>
      </v-col>
    </v-row>

    <v-btn
      color="primary mr-3"
      v-on:click="save()"
      :loading="loading"
      :disabled="!!readonly"
    >
      <v-icon left>{{ $props.button_icon }}</v-icon>
      {{ $t(`source-adapters.${$props.button_title}`) }}
    </v-btn>
  </v-form>
</template>

<script>
import { rules } from "../../../mixins/rules";
import { projectPermissions } from "../../../mixins/projects/projectPermissions";
import { codemirror } from "vue-codemirror";
import ConfigSourceAdapter from "./ConfigSourceAdapter";
export default {
  name: "FormSourceAdapter",
  components: { codemirror, ConfigSourceAdapter },
  mixins: [rules, projectPermissions],
  props: {
    dispatch: String,
    button_title: String,
    button_icon: String,
    sourceAdapter: {
      default: function() {
        return {
          type: ""
        };
      }
    },
    resourceLoading: {
      type: Boolean,
      default: false
    },
    permission: {
      type: String,
      default: ""
    }
  },
  data: () => ({
    loading: false,
    options: {
      lineNumbers: true,
      foldGutter: true,
      gutters: ["CodeMirror-linenumbers", "CodeMirror-foldgutter"],
      mode: "application/json"
    },
    config: "",
    tabIndex: 0,
    type: ""
  }),
  computed: {
    readonly: {
      get() {
        return !this.hasCurrentProjectPermissions(this.permission);
      }
    },
    types: {
      get() {
        return [
          "generic-accumulator",
          "generic-session-accumulator",
          "e-meter",
          "zaptec-ev-charger",
          "lansen-bridge",
          "ocpp-proxy"
        ].map(id => ({
          title: this.$t("source-adapters.types." + id),
          value: id
        }));
      }
    }
  },
  methods: {
    save() {
      this.clearBackEndErrors();
      if (this.$refs.form.validate()) {
        this.loading = true;
        let item = {
          ...this.sourceAdapter,
          config: this.config,
          project_id: this.$route.params.project_id
        };
        this.$store
          .dispatch(this.$props.dispatch, {
            item,
            errorHandler: this.errorHandler.bind(this, () => {
              this.$refs.form.validate();
            }),
            onSuccess: i => {
              if (i !== "") {
                this.$store.commit(
                  "projectMeteringStore/sourceAdapterStore/set",
                  {
                    item: i
                  }
                );
                this.$router.push({
                  name: "source-adapter",
                  params: {
                    id: i.id,
                    project_id: this.$route.params.project_id
                  }
                });
              } else {
                this.$store.commit(
                  "projectMeteringStore/sourceAdapterStore/set",
                  {
                    item
                  }
                );
              }
            }
          })
          .finally(() => (this.loading = false));
      }
    },
    formConfig() {
      this.config =
        (this.sourceAdapter &&
          this.sourceAdapter.config &&
          JSON.stringify(JSON.parse(this.sourceAdapter.config), null, 4)) ||
        "{}";
    }
  },
  watch: {
    sourceAdapter() {
      this.formConfig();
      this.type = this.sourceAdapter.type;
    },
    type() {
      this.sourceAdapter.type = this.type;
      let config =
        (this.sourceAdapter.config && JSON.parse(this.sourceAdapter.config)) ||
        {};
      if (config) {
        if (this.type === "generic-accumulator") {
          config = {
            filters: config.filters || []
          };
        } else if (this.type === "generic-session-accumulator") {
          config = {
            tokens: config.tokens || [],
            devices: config.devices || []
          };
        } else if (this.type === "e-meter") {
          config = {
            tokens: config.tokens || [],
            devices: config.devices || []
          };
        } else if (this.type === "zaptec-ev-charger") {
          config = {
            tokens: config.tokens || [],
            chargers: config.chargers || []
          };
        } else if (this.type === "ocpp-proxy") {
          config = {
            tokens: config.tokens || [],
            chargers: config.chargers || [],
            centralSystemUrl: config.centralSystemUrl || ""
          };
        } else if (this.type === "lansen-bridge") {
          config = {
            devices: config.devices || [],
            encryptionKeys: config.encryptionKeys || [],
            configurationPassword: config.configurationPassword || ""
          };
        }
      }
      this.sourceAdapter.config = JSON.stringify(config);
      this.formConfig();
    },
    tabIndex() {
      setTimeout(
        () => this.$refs.codemirror && this.$refs.codemirror.refresh(),
        500
      );
    }
  },
  mounted() {
    this.formConfig();
    this.type = this.sourceAdapter.type;
  }
};
</script>
<style lang="scss" scoped>
.config-error-bag::v-deep .v-input__slot {
  display: none !important;
}
</style>
